import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { buildWebPageJsonLd } from '@/lib/seo';
import { categories, kaspreadsheetUrl } from '@/mocks/categories';

export default function ShoppingGuide() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <JsonLd data={buildWebPageJsonLd({ headline: 'BaseTao Shopping Guide: How to Find, Compare, and Buy Products Smart', description: 'Your complete shopping companion for navigating BaseTao product categories and making informed purchasing decisions.', path: '/basetao-shopping-guide' })} />
      <Navbar />
      <main className="pt-20">
        <article>
          <header className="bg-background-100 py-14 md:py-20">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <span>/</span>
                <span className="text-foreground-700">Shopping Guide</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                BaseTao Shopping Guide: How to Find, Compare, and Buy Products Smart
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-3xl leading-relaxed mb-6">
                Your complete shopping companion for navigating BaseTao product categories. Learn how to effectively browse categories, compare products across suppliers, understand pricing, and make informed purchasing decisions with confidence.
              </p>
              <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span>Updated: August 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                <span>12 min read</span>
              </div>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="space-y-10 text-foreground-700">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">How to Navigate BaseTao Product Categories</h2>
                <p className="text-sm md:text-base leading-relaxed mb-3">
                  BaseTao organizes products into carefully curated categories that make browsing intuitive and efficient. Whether you are looking for the latest sneaker releases, premium hoodies and sweaters, classic t-shirts, stylish jackets, comfortable pants and shorts, or fashion accessories — each category connects you directly to a curated selection of products from trusted suppliers. Understanding how to navigate these categories effectively is the first step toward becoming a confident and efficient shopper.
                </p>
                <p className="text-sm md:text-base leading-relaxed mb-3">
                  Each category page features advanced filtering options that allow you to narrow down products by price range, supplier rating, shipping availability, and other relevant attributes. Learning to use these filters effectively can reduce your browsing time significantly while helping you discover products that perfectly match your requirements.
                </p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Browse All Product Categories</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-200 hover:bg-primary-50 transition-all duration-200 cursor-pointer text-center group"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                      </div>
                      <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-700 transition-colors leading-tight">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">Smart Shopping Tips for BaseTao Users</h2>
                <div className="space-y-4">
                  {[
                    { title: 'Compare Before You Buy', desc: 'Always check the same product across multiple supplier listings. BaseTao Spreadsheet makes side-by-side comparison effortless, highlighting differences in pricing, shipping estimates, and supplier ratings so you can make informed choices.' },
                    { title: 'Watch for Seasonal Pricing Trends', desc: 'Product prices fluctuate throughout the year based on seasonal demand, promotional events, and inventory levels. Using the price history tracking feature helps you identify the optimal time to make your purchase.' },
                    { title: 'Factor in Total Landed Cost', desc: 'The displayed product price is only part of the equation. Always consider shipping costs, potential customs duties, and any applicable fees to understand the true total cost before placing an order.' },
                    { title: 'Start Small and Scale Up', desc: 'If you are new to a particular category or supplier, start with a smaller test order to evaluate product quality, shipping reliability, and overall experience before committing to larger purchases.' },
                  ].map((item) => (
                    <div key={item.title} className="bg-background-100 rounded-lg p-5 border border-background-200/60">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-accent-50 rounded-lg p-6 md:p-8 border border-accent-100">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-3">Start Shopping Now</h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                  Return to our <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">homepage</Link> for the complete product category overview and additional shopping resources.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a href={kaspreadsheetUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer">
                    Browse All Products
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line text-sm"></i></div>
                  </a>
                  <Link to="/" className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer">
                    Back to Homepage
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}